import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Button from '../../../ui/Button';
import ItemGrid from '../../../ui/ItemGrid';
import ItemSlot from '../../../ui/ItemSlot';
import GameAPI from '../GameAPI';
import { GameItemTooltip } from '../Game_ItemTooltip';
import { ItemArtwork } from '../ItemSlotContent';
import {
  createGroundLootOperationId,
  groundLootErrorMessage,
} from './groundLoot';
import GroundLootDialogFrame from './GroundLootDialogFrame';
import { isTouchPointer } from '../touchItemDrag';

const PACK_SIZE = 25;
const PACK_WIDTH = 5;

function secondsUntil(timestamp, now) {
  const milliseconds = Date.parse(timestamp) - now;
  return Number.isFinite(milliseconds) ? Math.max(0, Math.ceil(milliseconds / 1000)) : 0;
}

function GroundLootItem({
  entry,
  busy,
  item,
  now,
  nonExpiring,
  privateMode,
  onDragStart,
  onDragEnd,
  onTake,
}) {
  const anchorRef = useRef(null);
  const tooltipPinnedRef = useRef(false);
  const [tooltipInteractive, setTooltipInteractive] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const expiresIn = privateMode || nonExpiring
    ? null
    : secondsUntil(entry.expiresAtUtc, now);
  const graceIn = privateMode || nonExpiring
    ? null
    : secondsUntil(entry.lootableAtUtc, now);
  const canTake = entry.canTake
    && (privateMode || nonExpiring || expiresIn > 0);
  const status = privateMode
    ? (canTake ? 'Ready to claim' : 'Unavailable')
    : nonExpiring
      ? (canTake ? 'Ready to take' : 'Unavailable')
    : expiresIn <= 0
      ? 'Expired'
      : canTake
        ? `Expires in ${expiresIn}s`
        : graceIn > 0 ? `Owner-only for ${graceIn}s` : 'Unavailable';
  const closeTooltip = () => {
    tooltipPinnedRef.current = false;
    setTooltipInteractive(false);
    setTooltipVisible(false);
  };
  const showTooltip = interactive => {
    if (!interactive && tooltipPinnedRef.current) return;
    tooltipPinnedRef.current = interactive;
    setTooltipInteractive(interactive);
    setTooltipVisible(true);
  };

  return (
    <>
      <div
      aria-label={`${item?.name || 'Treasure item'}, quantity ${entry.quantity}. ${status}`}
      className="ground-loot-entry"
      draggable={!privateMode && canTake && !busy}
      onBlur={() => {
        if (!tooltipPinnedRef.current) closeTooltip();
      }}
      onContextMenu={event => {
        event.preventDefault();
        showTooltip(true);
      }}
      onDragStart={event => {
        event.dataTransfer?.setData('text/plain', entry.entryId);
        onDragStart(entry);
      }}
      onDragEnd={onDragEnd}
      onFocus={() => {
        showTooltip(false);
      }}
      onMouseEnter={() => {
        showTooltip(false);
      }}
      onMouseLeave={() => {
        if (!tooltipPinnedRef.current) closeTooltip();
      }}
      onPointerUp={event => {
        if (!isTouchPointer(event) || event.target.closest('button')) return;
        showTooltip(true);
      }}
      ref={anchorRef}
      role="group"
      tabIndex={0}
    >
      <ItemArtwork
        className="ground-loot-entry__image"
        item={item}
        itemId={entry.itemId}
      />
      <span className="ground-loot-entry__quantity">{entry.quantity}×</span>
      <span className="ground-loot-entry__status">{status}</span>
      <Button
        aria-label={`Take ${item?.name || 'item'}, quantity ${entry.quantity}`}
        className="ground-loot-entry__take"
        disabled={!canTake || busy}
        onClick={() => onTake(entry)}
        variant="primary"
      >
        Take
      </Button>
      </div>
      {tooltipVisible ? (
        <GameItemTooltip
          actions={[{
            id: 'take',
            label: 'Take',
            disabled: !canTake || busy,
            disabledReason: !canTake ? status : busy ? 'Another loot action is in progress.' : '',
            onSelect: () => {
              closeTooltip();
              onTake(entry);
            },
          }, ...(!privateMode ? [{
            id: 'place',
            label: 'Choose Pack slot',
            disabled: !canTake || busy,
            disabledReason: !canTake ? status : busy ? 'Another loot action is in progress.' : '',
            onSelect: () => {
              closeTooltip();
              onDragStart(entry);
            },
          }] : [])]}
          anchorRect={anchorRef.current?.getBoundingClientRect()}
          getAnchorElement={() => anchorRef.current}
          getAnchorRect={() => anchorRef.current?.getBoundingClientRect()}
          interactive={tooltipInteractive}
          itemId={entry.itemId}
          itemInstanceId={entry.itemInstanceId || ''}
          onRequestClose={closeTooltip}
        />
      ) : null}
    </>
  );
}

export default function GroundLootTransferDialog({
  busy = false,
  detail,
  error = '',
  inventory = [],
  loading = false,
  containerTitle = '',
  nonExpiring = false,
  onClose,
  onDrop,
  onRefresh,
  onTake,
  privateMode = false,
  serverTimeOffsetMs = 0,
  worldContainer = false,
}) {
  const [now, setNow] = useState(
    () => Date.now() + serverTimeOffsetMs,
  );
  const [draggedEntry, setDraggedEntry] = useState(null);
  const [draggedPackItem, setDraggedPackItem] = useState(null);
  const [itemsLoadedAt, setItemsLoadedAt] = useState(0);
  const [packDropBusy, setPackDropBusy] = useState(false);
  const [packDropError, setPackDropError] = useState('');
  const [packDropQuantity, setPackDropQuantity] = useState(1);
  const [packDropRequest, setPackDropRequest] = useState(null);
  const [packTooltip, setPackTooltip] = useState(null);
  const [treasureAnnouncement, setTreasureAnnouncement] = useState('');
  const packTooltipPinnedRef = useRef(false);
  const packDropFormRef = useRef(null);
  const packDropOpenerRef = useRef(null);
  const packDropOperationIdRef = useRef(createGroundLootOperationId());
  const packDropQuantityRef = useRef(null);
  const packGridRef = useRef(null);
  const previousEntrySignatureRef = useRef(null);
  const canDropFromPack = !privateMode
    && (!nonExpiring || worldContainer)
    && (!worldContainer || detail?.container?.canDeposit)
    && typeof onDrop === 'function';
  const entries = useMemo(
    () => (detail?.entries || []).filter(entry => (
      privateMode
      || nonExpiring
      || !entry.expiresAtUtc
      || Date.parse(entry.expiresAtUtc) > now
    )),
    [detail?.entries, nonExpiring, now, privateMode],
  );
  const entrySignature = entries
    .map(entry => `${entry.entryId}:${entry.quantity}`)
    .join('|');

  useEffect(() => {
    if (loading) return;
    const previousSignature = previousEntrySignatureRef.current;
    previousEntrySignatureRef.current = entrySignature;
    if (previousSignature === null || previousSignature === entrySignature) {
      return;
    }
    setTreasureAnnouncement(
      `${containerTitle || (privateMode ? 'Starter Cache' : 'Treasure')} updated; ${entries.length} item${entries.length === 1 ? ' remains' : 's remain'}.`,
    );
  }, [
    containerTitle,
    entries.length,
    entrySignature,
    loading,
    privateMode,
  ]);

  useEffect(() => {
    setNow(Date.now() + serverTimeOffsetMs);
    if (privateMode && !detail?.container?.refillAtUtc) {
      return undefined;
    }
    const timer = window.setInterval(
      () => setNow(Date.now() + serverTimeOffsetMs),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [
    detail?.container?.refillAtUtc,
    detail?.container?.openUntilUtc,
    privateMode,
    serverTimeOffsetMs,
  ]);

  useEffect(() => {
    if (
      draggedEntry
      && !entries.some(entry => entry.entryId === draggedEntry.entryId)
    ) {
      setDraggedEntry(null);
    }
  }, [draggedEntry, entries]);

  useEffect(() => {
    if (
      draggedPackItem
      && !inventory.some(item => (
        Number(item.x) === Number(draggedPackItem.x)
        && Number(item.y) === Number(draggedPackItem.y)
        && item.itemId === draggedPackItem.itemId
        && String(item.itemInstanceId || '')
          === String(draggedPackItem.itemInstanceId || '')
      ))
    ) {
      setDraggedPackItem(null);
    }
  }, [draggedPackItem, inventory]);

  useEffect(() => {
    if (packDropRequest && !canDropFromPack) {
      setPackDropRequest(null);
      setPackDropError('');
    }
  }, [canDropFromPack, packDropRequest]);

  useEffect(() => {
    if (!packDropRequest) return undefined;
    const timer = window.setTimeout(
      () => (
        packDropQuantityRef.current
        || packDropFormRef.current?.querySelector('button[type="submit"]')
      )?.focus({ preventScroll: true }),
      0,
    );
    return () => window.clearTimeout(timer);
  }, [packDropRequest]);

  useEffect(() => {
    if (privateMode) return undefined;
    const refreshTimes = (detail?.entries || [])
      .flatMap(entry => [entry.expiresAtUtc, entry.lootableAtUtc])
      .concat(detail?.container?.refillAtUtc || [])
      .concat(detail?.container?.openUntilUtc || [])
      .map(timestamp => Date.parse(timestamp))
      .filter(refreshAt => (
        Number.isFinite(refreshAt)
        && refreshAt > Date.now() + serverTimeOffsetMs
      ));
    if (!refreshTimes.length) return undefined;
    const timer = window.setTimeout(
      () => onRefresh(),
      Math.max(
        0,
        Math.min(...refreshTimes)
          - (Date.now() + serverTimeOffsetMs)
          + 100,
      ),
    );
    return () => window.clearTimeout(timer);
  }, [
    detail?.container?.refillAtUtc,
    detail?.container?.openUntilUtc,
    detail?.entries,
    onRefresh,
    privateMode,
    serverTimeOffsetMs,
  ]);

  useEffect(() => {
    let active = true;
    const markItemsLoaded = () => {
      if (active) {
        setItemsLoadedAt(current => Math.max(Date.now(), current + 1));
      }
    };
    const autoIds = [...(detail?.entries || []), ...inventory].map(item => ({
      itemId: item.itemId,
      itemInstanceId: item.itemInstanceId,
    }));
    GameAPI.fetchItemsAuto(autoIds)
      .then(() => {
        const rarities = autoIds
          .map(item => GameAPI.fetchItemAutoCached(item.itemId, item.itemInstanceId)?.rarityId);
        return GameAPI.fetchRarities(rarities);
      })
      .then(() => {
        markItemsLoaded();
      })
      .catch(() => {
        markItemsLoaded();
      });
    return () => { active = false; };
  }, [detail?.entries, inventory]);

  const capacity = Math.max(
    nonExpiring ? 1 : 5,
    Number(detail?.container?.capacity) || (nonExpiring ? 1 : 5),
  );
  const treasureSlots = Array.from({ length: capacity }, () => null);
  entries.forEach(entry => {
    if (entry.slotIndex >= 0 && entry.slotIndex < treasureSlots.length) {
      treasureSlots[entry.slotIndex] = entry;
    }
  });

  const packSlots = Array.from({ length: PACK_SIZE }, () => null);
  inventory.forEach(item => {
    const index = (Number(item.x) * PACK_WIDTH) + Number(item.y);
    if (index >= 0 && index < PACK_SIZE) packSlots[index] = item;
  });

  const tier = detail?.container?.tier || 'small';
  const title = containerTitle || (
    privateMode
      ? 'Starter Cache'
      : `${tier.charAt(0).toUpperCase()}${tier.slice(1)} Treasure Pack`
  );
  const refillSeconds = detail?.container?.refillAtUtc
    ? secondsUntil(detail.container.refillAtUtc, now)
    : null;
  const transferBusy = busy || packDropBusy;

  const focusAfterPackDrop = preferOpener => {
    const opener = packDropOpenerRef.current;
    const target = preferOpener && opener?.isConnected
      ? opener
      : packGridRef.current;
    window.setTimeout(
      () => target?.focus({ preventScroll: true }),
      0,
    );
  };

  const requestPackDrop = (item, opener = null, slot = null) => {
    if (!canDropFromPack || transferBusy || !item) return;
    const maximum = Math.max(1, Number(item.quantity) || 1);
    setDraggedPackItem(null);
    setPackDropRequest({ ...item, destinationSlot: slot });
    setPackDropQuantity(maximum);
    setPackDropError('');
    packDropOpenerRef.current = opener;
    packDropOperationIdRef.current = createGroundLootOperationId();
  };

  const closePackTooltip = () => {
    packTooltipPinnedRef.current = false;
    setPackTooltip(null);
  };

  const showPackTooltip = (item, anchor, interactive) => {
    if (!interactive && packTooltipPinnedRef.current) return;
    packTooltipPinnedRef.current = interactive;
    setPackTooltip({ item, anchor, interactive });
  };

  const finishPackTouch = (event, item) => {
    if (!isTouchPointer(event)) return;
    showPackTooltip(item, event.currentTarget, true);
  };

  const updatePackDropQuantity = value => {
    const maximum = Math.max(
      1,
      Number(packDropRequest?.quantity) || 1,
    );
    setPackDropQuantity(
      Math.min(maximum, Math.max(1, Number(value) || 1)),
    );
    setPackDropError('');
    packDropOperationIdRef.current = createGroundLootOperationId();
  };

  const confirmPackDrop = async event => {
    event.preventDefault();
    if (!packDropRequest || transferBusy || !canDropFromPack) return;
    setPackDropBusy(true);
    setPackDropError('');
    try {
      await onDrop(
        packDropRequest,
        packDropQuantity,
        packDropOperationIdRef.current,
        packDropRequest.destinationSlot,
      );
      setPackDropRequest(null);
      focusAfterPackDrop(false);
    } catch (dropError) {
      if (dropError?.groundLootUseNewOperationId) {
        packDropOperationIdRef.current = createGroundLootOperationId();
      }
      setPackDropError(groundLootErrorMessage(
        dropError,
        'The item could not be moved. The container and your Pack were refreshed; try again.',
      ));
    } finally {
      setPackDropBusy(false);
    }
  };

  return (
    <GroundLootDialogFrame
      busy={transferBusy}
      className="ground-loot-dialog--transfer"
      description={privateMode
        ? 'Take your private starter gear, then open your Pack and equip both items.'
        : nonExpiring
          ? worldContainer
            ? 'Move items both ways while this shared chest is open. Remaining contents spill to ground treasure when its timer ends.'
            : 'Take loot into your Pack. This shared container does not expire.'
        : canDropFromPack
          ? 'Move items both ways: drag treasure to a Pack slot, or drag a Pack item into this container. Buttons provide the same actions.'
          : 'Drag treasure into an exact Pack slot, or use Take for automatic placement.'}
      eyebrow={privateMode
        ? 'Private run equipment'
        : nonExpiring ? 'Shared world loot' : undefined}
      onClose={onClose}
      title={title}
    >
      {error ? <p className="ground-loot-dialog__error" role="alert">{error}</p> : null}
      <div
        aria-busy={loading || transferBusy || undefined}
        className="ground-loot-transfer"
        data-items-loaded-at={itemsLoadedAt || undefined}
      >
        <section aria-labelledby="ground-loot-treasure-heading" className="ground-loot-transfer__panel">
          <div className="ground-loot-transfer__panel-heading">
            <h3 id="ground-loot-treasure-heading">
              {privateMode
                ? 'Starter Cache'
                : nonExpiring ? title : 'Treasure Pack'}
            </h3>
            <span>{entries.length} / {capacity}</span>
          </div>
          {loading ? (
            <p className="ground-loot-transfer__state" role="status">
              {privateMode ? 'Opening Starter Cache…' : 'Opening treasure…'}
            </p>
          ) : (
            <>
              <ItemGrid
                className="ground-loot-grid ground-loot-grid--treasure"
                columns={PACK_WIDTH}
                fluid
                label={privateMode
                  ? 'Starter Cache contents'
                  : nonExpiring ? `${title} contents`
                  : 'Treasure Pack contents'}
                rows={Math.ceil(capacity / PACK_WIDTH)}
              >
                {treasureSlots.map((entry, slotIndex) => {
                  const item = entry
                    ? GameAPI.fetchItemAutoCached(entry.itemId, entry.itemInstanceId)
                    : null;
                  const rarity = item && GameAPI.fetchRarityCached(item.rarityId);
                  return (
                    <ItemSlot
                      aria-colindex={(slotIndex % PACK_WIDTH) + 1}
                      aria-label={`${privateMode ? 'Starter Cache' : nonExpiring ? title : 'Treasure'} slot ${slotIndex + 1}`}
                      aria-rowindex={Math.floor(slotIndex / PACK_WIDTH) + 1}
                      className={`ground-loot-slot${draggedPackItem && canDropFromPack ? ' is-drop-target' : ''}`}
                      item={item}
                      itemType={GameAPI.fetchItemTypeCached(item?.typeId)}
                      key={`treasure-${slotIndex}`}
                      onDragOver={event => {
                        if (draggedPackItem && canDropFromPack && !transferBusy) {
                          event.preventDefault();
                        }
                      }}
                      onClick={() => {
                        if (draggedPackItem && canDropFromPack && !transferBusy) {
                          requestPackDrop(draggedPackItem, null, slotIndex);
                        }
                      }}
                      onDrop={event => {
                        event.preventDefault();
                        if (!draggedPackItem || !canDropFromPack || transferBusy) {
                          return;
                        }
                        requestPackDrop(draggedPackItem, null, slotIndex);
                      }}
                      rarity={rarity?.name?.toLowerCase()}
                      rarityColor={rarity?.colorHex}
                      role="gridcell"
                    >
                      {entry ? (
                        <GroundLootItem
                          busy={transferBusy}
                          entry={entry}
                          item={item}
                          now={now}
                          nonExpiring={nonExpiring}
                          privateMode={privateMode}
                          onDragStart={setDraggedEntry}
                          onDragEnd={() => setDraggedEntry(null)}
                          onTake={onTake}
                        />
                      ) : null}
                    </ItemSlot>
                  );
                })}
              </ItemGrid>
              {entries.length === 0 ? (
                <p className="ground-loot-transfer__state">
                  {nonExpiring
                    ? worldContainer && detail?.container?.openUntilUtc
                      ? `${title} is empty but remains open for ${secondsUntil(detail.container.openUntilUtc, now)}s.`
                      : refillSeconds === null
                        ? `${title} is empty.`
                      : refillSeconds > 0
                        ? `${title} is empty. Refills in ${refillSeconds}s.`
                        : `${title} is refilling…`
                    : 'This treasure pack is empty.'}
                </p>
              ) : null}
            </>
          )}
        </section>

        <section aria-labelledby="ground-loot-pack-heading" className="ground-loot-transfer__panel">
          <div className="ground-loot-transfer__panel-heading">
            <h3 id="ground-loot-pack-heading">Your Pack</h3>
            <span>{inventory.length} / {PACK_SIZE}</span>
          </div>
          <ItemGrid
            className="ground-loot-grid ground-loot-grid--pack"
            columns={PACK_WIDTH}
            fluid
            label="Your Pack destination slots"
            ref={packGridRef}
            rows={PACK_SIZE / PACK_WIDTH}
            tabIndex={-1}
          >
            {packSlots.map((item, slotIndex) => {
              const row = Math.floor(slotIndex / PACK_WIDTH);
              const column = slotIndex % PACK_WIDTH;
              const itemDetails = item
                ? GameAPI.fetchItemAutoCached(item.itemId, item.itemInstanceId)
                : null;
              const rarity = itemDetails && GameAPI.fetchRarityCached(itemDetails.rarityId);
              const canAcceptDraggedEntry = !privateMode
                && Boolean(draggedEntry)
                && (
                  !item
                  || (
                  !draggedEntry.itemInstanceId
                  && !item.itemInstanceId
                  && draggedEntry.itemId === item.itemId
                  && Number(draggedEntry.quantity) + Number(item.quantity) <= 20
                  )
                );
              return (
                <ItemSlot
                  aria-colindex={column + 1}
                  aria-label={`Pack slot ${slotIndex + 1}${itemDetails ? `, ${itemDetails.name}` : ', empty'}`}
                  aria-rowindex={row + 1}
                  className={`ground-loot-slot ground-loot-slot--destination${canAcceptDraggedEntry ? ' is-drop-target' : ''}`}
                  item={itemDetails}
                  itemType={GameAPI.fetchItemTypeCached(itemDetails?.typeId)}
                  key={`pack-${slotIndex}`}
                  onDragOver={event => {
                    if (canAcceptDraggedEntry && !transferBusy) {
                      event.preventDefault();
                    }
                  }}
                  onClick={() => {
                    if (canAcceptDraggedEntry && !transferBusy) {
                      onTake(draggedEntry, { x: row, y: column });
                      setDraggedEntry(null);
                    }
                  }}
                  onDrop={event => {
                    event.preventDefault();
                    if (
                      !draggedEntry
                      || !canAcceptDraggedEntry
                      || transferBusy
                    ) return;
                    onTake(draggedEntry, { x: row, y: column });
                    setDraggedEntry(null);
                  }}
                  rarity={rarity?.name?.toLowerCase()}
                  rarityColor={rarity?.colorHex}
                  role="gridcell"
                >
                  {item ? (
                    <div
                      aria-label={`${itemDetails?.name || 'Pack item'}, quantity ${item.quantity}`}
                      className="ground-loot-pack-item"
                      draggable={canDropFromPack && !transferBusy}
                      onDragEnd={() => setDraggedPackItem(null)}
                      onDragStart={event => {
                        event.dataTransfer?.setData(
                          'text/plain',
                          `${item.itemInstanceId || item.itemId}:${item.x}:${item.y}`,
                        );
                        setDraggedPackItem(item);
                      }}
                      role="group"
                    >
                      <button
                        aria-label={`Inspect ${itemDetails?.name || 'Pack item'}, quantity ${item.quantity}`}
                        className="ground-loot-pack-item__details"
                        onBlur={() => {
                          if (!packTooltipPinnedRef.current) closePackTooltip();
                        }}
                        onContextMenu={event => {
                          event.preventDefault();
                          showPackTooltip(item, event.currentTarget, true);
                        }}
                        onFocus={event => showPackTooltip(item, event.currentTarget, false)}
                        onMouseEnter={event => showPackTooltip(item, event.currentTarget, false)}
                        onMouseLeave={() => {
                          if (!packTooltipPinnedRef.current) closePackTooltip();
                        }}
                        onPointerUp={event => finishPackTouch(event, item)}
                        type="button"
                      >
                        <ItemArtwork
                          className="ground-loot-entry__image"
                          item={itemDetails}
                          itemId={item.itemId}
                        />
                        <span className="ground-loot-entry__quantity">{item.quantity}×</span>
                      </button>
                      {canDropFromPack ? (
                        <Button
                          aria-label={`Move ${itemDetails?.name || 'Pack item'}, quantity ${item.quantity} to ${title}`}
                          className="ground-loot-pack-item__move"
                          disabled={transferBusy}
                          onClick={event => requestPackDrop(
                            item,
                            event.currentTarget,
                          )}
                          variant="danger"
                        >
                          Move
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </ItemSlot>
              );
            })}
          </ItemGrid>
        </section>
      </div>
      {packTooltip ? (
        <GameItemTooltip
          actions={canDropFromPack ? [{
            id: 'move-to-container',
            label: `Move to ${title}`,
            disabled: transferBusy,
            disabledReason: transferBusy ? 'Another loot action is in progress.' : '',
            onSelect: () => {
              const { item, anchor } = packTooltip;
              closePackTooltip();
              requestPackDrop(item, anchor);
            },
          }] : []}
          anchorRect={packTooltip.anchor?.getBoundingClientRect()}
          getAnchorElement={() => packTooltip.anchor}
          getAnchorRect={() => packTooltip.anchor?.getBoundingClientRect()}
          interactive={packTooltip.interactive}
          itemId={packTooltip.item.itemId}
          itemInstanceId={packTooltip.item.itemInstanceId || ''}
          onRequestClose={closePackTooltip}
        />
      ) : null}
      {packDropRequest ? (
        <form
          className="ground-loot-transfer__pack-drop"
          onSubmit={confirmPackDrop}
          ref={packDropFormRef}
        >
          <p>
            Move <strong>{GameAPI.fetchItemAutoCached(
              packDropRequest.itemId,
              packDropRequest.itemInstanceId,
            )?.name || 'Pack item'}</strong> into {title}?
          </p>
          {Number(packDropRequest.quantity) > 1 ? (
            <div className="ground-loot-drop-form__quantity">
              <label htmlFor="ground-loot-transfer-drop-quantity">
                Amount to move
              </label>
              <div>
                <input
                  id="ground-loot-transfer-drop-quantity"
                  inputMode="numeric"
                  max={packDropRequest.quantity}
                  min="1"
                  onChange={event => updatePackDropQuantity(event.target.value)}
                  ref={packDropQuantityRef}
                  type="number"
                  value={packDropQuantity}
                />
                <Button
                  aria-pressed={packDropQuantity === Number(packDropRequest.quantity)}
                  disabled={transferBusy}
                  onClick={() => updatePackDropQuantity(packDropRequest.quantity)}
                  variant="secondary"
                >
                  All ({packDropRequest.quantity})
                </Button>
              </div>
            </div>
          ) : (
            <p className="ground-loot-drop-form__single">Amount: 1</p>
          )}
          {packDropError ? (
            <p className="ground-loot-dialog__error" role="alert">
              {packDropError}
            </p>
          ) : null}
          <div className="ground-loot-dialog__actions">
            <Button
              disabled={transferBusy}
              onClick={() => {
                setPackDropRequest(null);
                setPackDropError('');
                focusAfterPackDrop(true);
              }}
              variant="ghost"
            >
              Keep in Pack
            </Button>
            <Button disabled={transferBusy} type="submit" variant="danger">
              {packDropBusy ? 'Moving…' : `Move ${packDropQuantity}`}
            </Button>
          </div>
        </form>
      ) : null}
      <div className="ground-loot-dialog__footer">
        <span aria-atomic="true" aria-live="polite" role="status">
          {transferBusy
            ? 'Transferring item…'
            : treasureAnnouncement || (
              privateMode
                ? 'Only you can claim this cache; it does not expire.'
                : nonExpiring
                  ? worldContainer && detail?.container?.openUntilUtc
                    ? `Shared chest closes in ${secondsUntil(detail.container.openUntilUtc, now)}s.`
                    : 'Contents are shared and do not expire.'
                : 'Treasure updates live for everyone nearby.'
            )}
        </span>
        <Button disabled={transferBusy} onClick={onClose} variant="secondary">Close</Button>
      </div>
    </GroundLootDialogFrame>
  );
}
